import { identity, pipe, ReadonlyArray, Runtime } from "fp";

import * as Data from "~/Data";
import { Name as DriverName } from "~/Driver";
import { Route as GenericRoute } from "~/Route";
import { Time } from "~/Time";

import * as Active from "./Active";
import * as Search from "./Search";
import * as Sort from "./Sort";

export * as Action from "./Action";
export * as Active from "./Active";
export * as Detail from "./Detail";
export * as Page from "./Page";
export * as Request from "./Request";
export * as Search from "./Search";
export * as Sort from "./Sort";
export * as Table from "./Table";

export type DocumentDriver = Data.Reference.Reference & {
  readonly name?: DriverName.Name;
};

export type Document = Data.Reference.Reference &
  Time.DateTime & {
    readonly driver: DocumentDriver;
    readonly code: string;
    readonly type: string;
    readonly reference: string;
    readonly properties?: readonly Property.Property[];
    readonly images?: readonly Images.Image[];
  };

export const driverName = (document: Document) =>
  document?.driver
    ? DriverName.toString(document.driver.name)
    : "Not available";

export const equals = (a: Data.Reference.Reference) => (
  b: Data.Reference.Reference
): boolean => a === b;

export const useStore = (): Data.Store.Store<Document> =>
  Data.Store.useSelector(({ documents: { store } }) => store);

export const useAllSorted = (): readonly Document[] =>
  pipe(
    {
      store: useStore(),
      sort: Sort.useActive(),
    },

    ({ store, sort }) =>
      pipe(
        store,
        Data.Store.toCache,
        Data.Cache.toList((_id, document) => document),
        ReadonlyArray.sort(Sort.Type.toOrdering(sort.type)),
        sort.direction === Sort.Direction.descending()
          ? ReadonlyArray.reverse
          : identity
      )
  );

export namespace Route {
  export const definition = () => "/documents" as const;
  export const href = () => definition();
  export const use = () => GenericRoute.use(href());
}

export namespace State {
  export type State = {
    readonly active: Active.State.State;
    readonly store: Data.Store.Store<Document>;
    readonly search: Search.Search;
    readonly sort: Sort.State.State;
  };

  export const initial = (): State => ({
    active: Active.State.initial(),
    store: Data.Store.initial(),
    search: Search.State.initial(),
    sort: Sort.State.initial(),
  });
}

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      id: Runtime.number(),
      first_name: Runtime.string(),
      last_name: Runtime.string(),
      code: Runtime.string(),
      driver_id: Runtime.number(),
      type: Runtime.string(),
      date: Time.DateTime.Response.runtime(),
      reference: Runtime.string(),
      properties: Runtime.nullable(Runtime.array(Property.Response.runtime())),
      images: Runtime.nullable(Runtime.array(Images.runtime())),
    });

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Document => ({
    ...response.date,
    id: `${response.id}`,
    driver: {
      id: `${response.driver_id}`,
      name: DriverName.Response.decode(response),
    },
    code: response.code,
    type: response.type,
    reference: response.reference,
    properties: response.properties
      ? response.properties.map(Property.Response.decode)
      : [],
    images: response.images ? response.images.map(Images.decode) : [],
  });
}

export namespace Property {
  export type Property = Data.Reference.Reference & {
    readonly documentId: string;
    readonly name: string;
    readonly value: string;
  };

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        document_property_id: Runtime.number(),
        document_id: Runtime.number(),
        property_name: Runtime.string(),
        property_value: Runtime.string(),
      });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Property => ({
      id: `${response.document_property_id}`,
      documentId: `${response.document_id}`,
      name: response.property_name,
      value: response.property_value,
    });
  }
}

export namespace Images {
  export type Image = {
    readonly documentImageId: string;
    readonly documentId: string;
    readonly imageUrl: string;
  };

  export const runtime = () =>
    Runtime.strict({
      document_image_id: Runtime.number(),
      document_id: Runtime.number(),
      image_url: Runtime.string(),
    });

  export const decode = (response: Runtime.ToType<typeof runtime>): Image => ({
    documentImageId: `${response.document_image_id}`,
    documentId: `${response.document_id}`,
    imageUrl: response.image_url,
  });
}
